import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Stack,
  Pagination,
  useMediaQuery
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import styles from "./Roles.module.css";

const RoleEdit = () => {
  const { roleid } = useParams();
  const isCreate = roleid === undefined || roleid === null;
  const roleIdNum = isCreate ? null : Number(roleid);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const [role, setRole] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [isSavingRole, setIsSavingRole] = useState(false);
  const [draft, setDraft] = useState({ RoleDisplayName: "", RoleName: "", RoleDescription: "" });

  // Add user dialog state
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [isUserSearching, setIsUserSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isAssigningUser, setIsAssigningUser] = useState(false);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState([]);

  const isNotFound = (e) => e?.response?.status === 404;

  const formatApiError = (e) =>
    e?.response?.data?.error ||
    (typeof e?.response?.data === "string" ? e.response.data : null) ||
    e?.message ||
    "Unknown error";

  const fetchRoleUsersFallback = useCallback(
    async (roleId) => {
      // Fallback for backends that don't implement GET /roles/:roleId/users.
      // Uses GET /users and filters client-side by CMS_UserRole.RoleID.
      const all = [];
      let page = 1;
      const limit = 250;
      let totalPages = 1;

      // Cap pages to avoid hammering the API in huge datasets
      const MAX_PAGES = 10;

      while (page <= totalPages && page <= MAX_PAGES) {
        const resp = await axiosPrivate.get("/api/users", { params: { page, limit, search: "" } });
        const list = Array.isArray(resp?.data?.users) ? resp.data.users : [];
        const pg = resp?.data?.pagination;
        totalPages = Number(pg?.totalPages || 1);
        all.push(...list);
        page += 1;
      }

      if (totalPages > MAX_PAGES) {
        enqueueSnackbar(
          `User list truncated while loading role members (showing first ${MAX_PAGES} pages).`,
          { variant: "warning" }
        );
      }

      return all
        .map((u) => {
          const links = Array.isArray(u?.CMS_UserRole) ? u.CMS_UserRole : [];
          const match = links.find((l) => Number(l?.RoleID) === Number(roleId));
          if (!match) return null;
          return {
            UserID: u?.UserID,
            FullName: u?.FullName || `${u?.FirstName || ""} ${u?.LastName || ""}`.trim(),
            Email: u?.Email || "",
            UserName: u?.UserName || "",
            // needed for fallback removal via basic endpoint
            UserRoleID: match?.UserRoleID
          };
        })
        .filter(Boolean);
    },
    [axiosPrivate, enqueueSnackbar]
  );

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      if (isCreate) {
        setRole(null);
        setDraft({ RoleDisplayName: "", RoleName: "", RoleDescription: "" });
        setUsers([]);
        return;
      }

      if (!roleIdNum || Number.isNaN(roleIdNum)) {
        setError(true);
        return;
      }

      // Role details: prefer /api/roles/:id; fallback to basic model endpoint
      let r = null;
      try {
        const roleResp = await axiosPrivate.get(`/api/roles/${roleIdNum}`);
        r = roleResp?.data || null;
      } catch (e) {
        if (!isNotFound(e)) throw e;
        // Avoid /api/basic/cms_role/:id because some backends have a broken ID mapping (ItemID vs RoleID).
        // Instead, load the list and select by RoleID.
        let listResp;
        try {
          listResp = await axiosPrivate.get('/api/roles');
        } catch (e2) {
          if (!isNotFound(e2)) throw e2;
          listResp = await axiosPrivate.get('/api/basic/cms_role');
        }
        const list = Array.isArray(listResp?.data) ? listResp.data : [];
        r = list.find((x) => Number(x?.RoleID) === Number(roleIdNum)) || null;
        if (!r) {
          throw new Error('Role not found');
        }
      }

      // Role users: prefer /api/roles/:id/users; fallback to filtering /api/users
      let u = [];
      try {
        const usersResp = await axiosPrivate.get(`/api/roles/${roleIdNum}/users`);
        u = Array.isArray(usersResp?.data) ? usersResp.data : [];
      } catch (e) {
        if (!isNotFound(e)) throw e;
        u = await fetchRoleUsersFallback(roleIdNum);
      }

      setRole(r);
      setDraft({
        RoleDisplayName: r?.RoleDisplayName ?? "",
        RoleName: r?.RoleName ?? "",
        RoleDescription: r?.RoleDescription ?? ""
      });
      setUsers(u);
    } catch (e) {
      setError(true);
      enqueueSnackbar(formatApiError(e) || "Failed to load role", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate, enqueueSnackbar, fetchRoleUsersFallback, isCreate, roleIdNum]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Debounce user search input
  useEffect(() => {
    if (!isAddUserOpen) return;
    const t = setTimeout(() => setDebouncedUserSearch(userSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [userSearch, isAddUserOpen]);

  // Fetch users for selector (search)
  useEffect(() => {
    if (!isAddUserOpen) return;
    let cancelled = false;
    const run = async () => {
      setIsUserSearching(true);
      try {
        const resp = await axiosPrivate.get("/api/users", {
          params: { page: 1, limit: 25, search: debouncedUserSearch, basic: 1 }
        });
        const list = resp?.data?.users || [];
        if (!cancelled) setUserOptions(list);
      } catch (e) {
        if (!cancelled) {
          setUserOptions([]);
          enqueueSnackbar("User search failed", { variant: "error" });
        }
      } finally {
        if (!cancelled) setIsUserSearching(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [axiosPrivate, debouncedUserSearch, enqueueSnackbar, isAddUserOpen]);

  const filteredUsers = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    return (users || []).filter((u) => {
      const name = String(u?.FullName ?? "").toLowerCase();
      const email = String(u?.Email ?? "").toLowerCase();
      const username = String(u?.UserName ?? "").toLowerCase();
      return name.includes(lower) || email.includes(lower) || username.includes(lower);
    });
  }, [searchTerm, users]);

  // Sort the ENTIRE filtered list (not just the current page) before paginating.
  const sortedUsers = useMemo(() => {
    if (!sortModel.length) return filteredUsers;
    const { field, sort } = sortModel[0];
    if (!field || !sort) return filteredUsers;
    const dir = sort === "desc" ? -1 : 1;
    return [...filteredUsers].sort((a, b) => {
      const av = String(a?.[field] ?? "").toLowerCase();
      const bv = String(b?.[field] ?? "").toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filteredUsers, sortModel]);

  const handleSaveRole = async () => {
    const payload = {
      RoleDisplayName: String(draft.RoleDisplayName ?? "").trim(),
      RoleName: String(draft.RoleName ?? "").trim(),
      RoleDescription: String(draft.RoleDescription ?? "").trim()
    };
    if (!payload.RoleDisplayName || !payload.RoleName) {
      enqueueSnackbar("Role Display Name and Role Name are required", { variant: "warning" });
      return;
    }

    setIsSavingRole(true);
    try {
      if (isCreate) {
        const created = await axiosPrivate.post(`/api/roles`, payload);
        const newId = created?.data?.RoleID;
        enqueueSnackbar("Role created", { variant: "success" });
        if (newId) {
          navigate(`/admin/roles/edit/${newId}`, { replace: true });
          return;
        }
      } else {
        await axiosPrivate.put(`/api/roles/${roleIdNum}`, payload);
        enqueueSnackbar("Role updated", { variant: "success" });
      }
      await fetchAll();
    } catch (e) {
      enqueueSnackbar(formatApiError(e) || "Failed to save role", { variant: "error" });
    } finally {
      setIsSavingRole(false);
    }
  };

  const handleRemoveUser = useCallback(
    async (row) => {
      if (isCreate) return;
      const userId = row?.UserID;
      if (!userId) return;
      try {
        try {
          await axiosPrivate.delete(`/api/roles/${roleIdNum}/users/${userId}`);
        } catch (e) {
          if (!isNotFound(e)) throw e;
          // Fallback: delete the specific CMS_UserRole row by UserRoleID
          if (!row?.UserRoleID) {
            throw new Error("Missing UserRoleID for fallback removal");
          }
          await axiosPrivate.delete(`/api/basic/cms_userrole/${row.UserRoleID}`);
        }
        enqueueSnackbar("User removed from role", { variant: "success" });
        setUsers((prev) => (prev || []).filter((u) => u.UserID !== userId));
      } catch (e) {
        enqueueSnackbar(formatApiError(e) || "Failed to remove user", { variant: "error" });
      }
    },
    [axiosPrivate, enqueueSnackbar, isCreate, roleIdNum]
  );

  const openAddUser = () => {
    setSelectedUser(null);
    setUserSearch("");
    setDebouncedUserSearch("");
    setUserOptions([]);
    setIsAddUserOpen(true);
  };

  const closeAddUser = () => {
    if (isAssigningUser) return;
    setIsAddUserOpen(false);
  };

  const getUserLabel = (u) => {
    if (!u) return "";
    const full = String(u.FullName || `${u.FirstName || ""} ${u.LastName || ""}`).trim();
    return full || u.UserName || String(u.UserID || "");
  };

  const handleAssignUser = async () => {
    if (isCreate) {
      enqueueSnackbar("Create the role first, then add users.", { variant: "info" });
      return;
    }
    if (!selectedUser?.UserID) return;
    setIsAssigningUser(true);
    try {
      try {
        await axiosPrivate.post(`/api/roles/${roleIdNum}/users`, { userId: selectedUser.UserID });
      } catch (e) {
        if (!isNotFound(e)) throw e;
        await axiosPrivate.post(`/api/basic/cms_userrole`, {
          UserID: selectedUser.UserID,
          RoleID: roleIdNum
        });
      }
      enqueueSnackbar("User added to role", { variant: "success" });
      setIsAddUserOpen(false);
      await fetchAll();
    } catch (e) {
      enqueueSnackbar(formatApiError(e) || "Failed to add user", { variant: "error" });
    } finally {
      setIsAssigningUser(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        getActions: (params) => [
          <GridActionsCellItem
            key="remove"
            icon={<DeleteIcon />}
            label="Remove"
            onClick={() => handleRemoveUser(params.row)}
          />
        ]
      },
      { field: "FullName", headerName: "Name", minWidth: 220, flex: 1 },
      { field: "Email", headerName: "Email", minWidth: 260, flex: 1.2 },
      { field: "UserName", headerName: "Username", minWidth: 180, flex: 0.8 }
    ],
    [handleRemoveUser]
  );

  if (isLoading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
        <Typography className={styles.loadingText}>Loading role...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.errorContainer}>
        <Typography color="error">Error occurred while loading role, Please contact an admin</Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Paper className={styles.searchPaper}>
        <Box className={styles.searchContainer}>
          <Box
            className={styles.buttonContainer}
            sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'center', gap: 1 }}
          >
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate("/admin/roles")}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddUser}
              className={styles.addButton}
              disabled={isCreate}
              sx={{ flex: { xs: 1, sm: 'none' }, whiteSpace: 'nowrap' }}
            >
              Add User
            </Button>
          </Box>

          <Typography variant="h5" sx={{ marginTop: 1 }}>
            {isCreate ? "New Role" : `Edit Role: ${role?.RoleDisplayName || `#${roleIdNum}`}`}
          </Typography>

          <TextField
            label="Role Display Name"
            placeholder="Role Display Name"
            value={draft.RoleDisplayName}
            onChange={(e) => setDraft((p) => ({ ...p, RoleDisplayName: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Role Name"
            placeholder="Role Name"
            value={draft.RoleName}
            onChange={(e) => setDraft((p) => ({ ...p, RoleName: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Role Description"
            placeholder="Role Description"
            multiline
            minRows={3}
            value={draft.RoleDescription}
            onChange={(e) => setDraft((p) => ({ ...p, RoleDescription: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSaveRole}
              disabled={isSavingRole}
              className={styles.confirmButton}
            >
              {isSavingRole ? "Saving..." : "Save Role"}
            </Button>
          </Box>

          <TextField
            className={styles.searchField}
            label="Search users"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Paper>

      <Paper className={styles.dataGridPaper}>
        <DataGrid
          className="rolesDataGrid"
          rows={sortedUsers.slice(
            paginationModel.page * paginationModel.pageSize,
            (paginationModel.page + 1) * paginationModel.pageSize
          )}
          columns={columns}
          getRowId={(row) => row.UserID}
          disableRowSelectionOnClick
          autoHeight
          hideFooterPagination
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(m) => {
            setSortModel(m);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        />
      </Paper>

      {/* Custom Pagination */}
      <Paper className={styles.paginationPaper}>
        <Stack
          className={styles.paginationStack}
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {filteredUsers.length > 0 ? (paginationModel.page * paginationModel.pageSize) + 1 : 0} to{' '}
            {Math.min((paginationModel.page + 1) * paginationModel.pageSize, filteredUsers.length)} of {filteredUsers.length} users
          </Typography>

          <Stack className={styles.paginationControls} direction="row" alignItems="center">
            <Pagination
              count={Math.ceil(filteredUsers.length / paginationModel.pageSize) || 1}
              page={paginationModel.page + 1}
              onChange={(_, page) => setPaginationModel((prev) => ({ ...prev, page: page - 1 }))}
              color="primary"
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
              className="rolesPagination"
            />
          </Stack>

          <Box className={styles.pageSizeSelector}>
            <Typography variant="body2" color="text.secondary">Show:</Typography>
            <TextField
              select
              size="small"
              value={paginationModel.pageSize}
              onChange={(e) => {
                const newLimit = parseInt(e.target.value);
                setPaginationModel((prev) => ({ ...prev, pageSize: newLimit, page: 0 }));
              }}
              SelectProps={{ native: true }}
              className={`${styles.pageSizeField} pageSizeField`}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </TextField>
            <Typography variant="body2" color="text.secondary">per page</Typography>
          </Box>
        </Stack>
      </Paper>

      <Dialog open={isAddUserOpen} onClose={closeAddUser} fullWidth maxWidth="sm">
        <DialogTitle className={styles.dialogTitle}>Add User to Role</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={userOptions}
            loading={isUserSearching}
            value={selectedUser}
            onChange={(_, v) => setSelectedUser(v)}
            inputValue={userSearch}
            onInputChange={(_, v) => setUserSearch(v)}
            getOptionLabel={getUserLabel}
            renderInput={(params) => (
              <TextField {...params} label="Search users" placeholder="Search users" InputLabelProps={{ shrink: true }} />
            )}
          />
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={closeAddUser} disabled={isAssigningUser} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignUser}
            disabled={isAssigningUser || !selectedUser}
            variant="contained"
            className={styles.confirmButton}
            startIcon={isAssigningUser ? <CircularProgress color="inherit" size={16} /> : <AddIcon />}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleEdit;


